import { INVITABLE_ROLES, type InvitationDto, type MemberDto } from "@clinic/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router";
import { api } from "../api";
import { fieldErrors, hasFieldErrors } from "../forms";
import { formatDate, useT } from "../i18n";
import { canManage } from "../session";
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Spinner } from "../ui";
import { useRequiredMe } from "./app";

/** Members and invitations of the active clinic (CB-23). Owner/admin only. */
export function TeamPage() {
  const me = useRequiredMe();
  const { t, tError, locale } = useT();
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ["members"], queryFn: () => api<{ items: MemberDto[] }>("GET", "/members") });
  const invitations = useQuery({
    queryKey: ["invitations"],
    queryFn: () => api<{ items: InvitationDto[] }>("GET", "/invitations"),
  });
  const invite = useMutation({
    mutationFn: (input: { email: string; role: string }) => api<InvitationDto>("POST", "/invitations", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invitations"] }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/invitations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invitations"] }),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api<void>("DELETE", `/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members"] }),
  });
  if (!canManage(me)) return <Navigate to="/app" replace />;
  const fields = fieldErrors(invite.error);
  const isOwner = me.memberships.find((m) => m.clinicId === me.activeClinicId)?.role === "owner";

  return (
    <div className="space-y-6">
      <PageHeader title={t("team.title")} subtitle={t("team.subtitle")} />

      <Card className="p-0">
        <h2 className="px-6 pt-5 font-semibold">{t("team.members")}</h2>
        {members.isPending ? (
          <div className="p-6">
            <Spinner />
          </div>
        ) : members.isError ? (
          <div className="p-6">
            <Alert>{tError(members.error)}</Alert>
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200">
            {members.data.items.map((m) => (
              <li key={m.userId} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
                <div>
                  <p className="font-medium">
                    {m.fullName}
                    <span className="ml-2">
                      <Badge tone={m.role === "owner" ? "green" : "gray"}>{t(`team.role_${m.role}`)}</Badge>
                    </span>
                  </p>
                  <p className="text-sm text-slate-500">{m.email}</p>
                </div>
                {m.userId !== me.user.id && (m.role !== "owner" || isOwner) ? (
                  <Button
                    type="button"
                    variant="ghost"
                    busy={remove.isPending && remove.variables === m.userId}
                    onClick={() => {
                      if (window.confirm(t("team.remove_confirm", { name: m.fullName }))) remove.mutate(m.userId);
                    }}
                  >
                    {t("team.remove")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {remove.error ? (
          <div className="px-6 pb-4">
            <Alert>{tError(remove.error)}</Alert>
          </div>
        ) : null}
      </Card>

      <Card>
        <h2 className="font-semibold">{t("team.invite_title")}</h2>
        <form
          className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
            invite.mutate({ email: v.email ?? "", role: v.role ?? "practitioner" }, { onSuccess: () => form.reset() });
          }}
        >
          {invite.isSuccess ? (
            <div className="sm:col-span-3">
              <Alert tone="success">{t("team.invited")}</Alert>
            </div>
          ) : null}
          {invite.error && !hasFieldErrors(invite.error) ? (
            <div className="sm:col-span-3">
              <Alert>{tError(invite.error)}</Alert>
            </div>
          ) : null}
          <Field label={t("team.invite_email")} name="email" type="email" required error={fields.email} />
          <Select label={t("team.invite_role")} name="role" defaultValue="practitioner" error={fields.role}>
            {INVITABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`team.role_${r}`)}
              </option>
            ))}
          </Select>
          <Button busy={invite.isPending}>{t("team.invite_button")}</Button>
          <p className="text-sm text-slate-500 sm:col-span-3">
            {t("team.role_admin")}: {t("team.role_admin_hint")} {t("team.role_practitioner")}:{" "}
            {t("team.role_practitioner_hint")}
          </p>
        </form>
      </Card>

      <Card className="p-0">
        <h2 className="px-6 pt-5 font-semibold">{t("team.invitations")}</h2>
        {invitations.isPending ? (
          <div className="p-6">
            <Spinner />
          </div>
        ) : invitations.isError || invitations.data.items.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">{t("team.invitations_empty")}</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200">
            {invitations.data.items.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
                <div>
                  <p className="font-medium">
                    {i.email} <Badge tone="gray">{t(`team.role_${i.role}`)}</Badge>
                  </p>
                  <p className="text-sm text-slate-500">
                    {t("team.expires", { date: formatDate(locale, i.expiresAt, undefined, { year: "numeric" }) })}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  busy={revoke.isPending && revoke.variables === i.id}
                  onClick={() => revoke.mutate(i.id)}
                >
                  {t("team.revoke")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
